// Markdown of Claude's answers and documents to HTML. Everything is escaped first; only headings, lists, tables,
// quotes, code, emphasis and links become markup. Links: http(s) open in a new tab, the pipeline's own schemes
// (deal:, mail:, anhang:, firma:, anfrage:, dok:) become buttons the views handle via data-link. Anything else stays
// text, so no HTML or script from a mail can get through.
import { esc } from "./util";

const SCHEMES = ["deal", "mail", "anhang", "firma", "anfrage", "dok"] as const;

function inline(s: string): string {
  let out = esc(s);
  // Code spans first, so their content is not formatted further.
  const codes: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_, c: string) => `\uE000${codes.push(`<code>${c}</code>`) - 1}\uE000`);
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (whole, label: string, target: string) => {
    const t = target.replace(/&amp;/g, "&");
    const scheme = t.split(":")[0]!.toLowerCase();
    if (/^https?:\/\//i.test(t)) return `<a href="${esc(t)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    if ((SCHEMES as readonly string[]).includes(scheme)) return `<button type="button" class="link md-link" data-link="${esc(t)}">${label}</button>`;
    return whole;
  });
  out = out.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/(^|[^*\w])\*(?!\s)(.+?)\*(?!\w)/g, "$1<i>$2</i>");
  return out.replace(/\uE000(\d+)\uE000/g, (_, i: string) => codes[Number(i)]!);
}

const cells = (row: string) =>
  row
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
const isTableSep = (l: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);

export function markdown(src: string): string {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const html: string[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length) html.push(`<p>${para.map(inline).join("<br>")}</p>`);
    para = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (/^```/.test(line)) {
      flush();
      const code: string[] = [];
      while (++i < lines.length && !/^```/.test(lines[i]!)) code.push(lines[i]!);
      html.push(`<pre><code>${esc(code.join("\n"))}</code></pre>`);
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      flush();
      const level = Math.min(h[1]!.length + 1, 5);
      html.push(`<h${level}>${inline(h[2]!)}</h${level}>`);
      continue;
    }
    if (line.includes("|") && isTableSep(lines[i + 1] ?? "")) {
      flush();
      const head = cells(line);
      const body: string[][] = [];
      i += 1;
      while (i + 1 < lines.length && lines[i + 1]!.includes("|") && lines[i + 1]!.trim()) body.push(cells(lines[++i]!));
      html.push(
        `<div class="md-table"><table><thead><tr>${head.map((c) => `<th>${inline(c)}</th>`).join("")}</tr></thead><tbody>${body
          .map((r) => `<tr>${head.map((_, j) => `<td>${inline(r[j] ?? "")}</td>`).join("")}</tr>`)
          .join("")}</tbody></table></div>`,
      );
      continue;
    }
    const li = /^(\s*)([-*•]|\d+[.)])\s+(.*)$/.exec(line);
    if (li) {
      flush();
      const ordered = /\d/.test(li[2]!);
      const items: string[] = [inline(li[3]!)];
      while (i + 1 < lines.length) {
        const next = /^(\s*)([-*•]|\d+[.)])\s+(.*)$/.exec(lines[i + 1]!);
        if (!next) break;
        items.push(inline(next[3]!));
        i++;
      }
      html.push(`<${ordered ? "ol" : "ul"}>${items.map((x) => `<li>${x}</li>`).join("")}</${ordered ? "ol" : "ul"}>`);
      continue;
    }
    if (/^>\s?/.test(line)) {
      flush();
      const quote: string[] = [line.replace(/^>\s?/, "")];
      while (i + 1 < lines.length && /^>\s?/.test(lines[i + 1]!)) quote.push(lines[++i]!.replace(/^>\s?/, ""));
      html.push(`<blockquote>${quote.map(inline).join("<br>")}</blockquote>`);
      continue;
    }
    if (/^\s*(-{3,}|\*{3,})\s*$/.test(line)) {
      flush();
      html.push("<hr>");
      continue;
    }
    if (!line.trim()) flush();
    else para.push(line);
  }
  flush();
  return html.join("");
}

/** The tables of a Markdown text as CSV for Excel (semicolon, BOM, quotes), one block per table; null without tables. */
export function tablesToCsv(src: string): string | null {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const blocks: string[][][] = [];
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i]!.includes("|") || !isTableSep(lines[i + 1] ?? "")) continue;
    const rows = [cells(lines[i]!)];
    i += 1;
    while (i + 1 < lines.length && lines[i + 1]!.includes("|") && lines[i + 1]!.trim()) rows.push(cells(lines[++i]!));
    blocks.push(rows);
  }
  if (!blocks.length) return null;
  const plain = (c: string) => c.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/`/g, "");
  const field = (c: string) => {
    let v = plain(c);
    // A cell starting like a formula would be run by Excel (CSV injection); numbers like -5 or +3,5 stay numbers.
    if (/^[=+\-@\t\r]/.test(v) && !/^[-+]?\d[\d.,]*$/.test(v)) v = `'${v}`;
    return /[;"\n']/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  };
  return `\uFEFF${blocks.map((b) => b.map((r) => r.map(field).join(";")).join("\r\n")).join("\r\n\r\n")}\r\n`;
}
